import axiosInstance from '@shared/utils/axios-instance';
import type { SellerAccountBlockCabinetInfo } from './seller-account-block.types';

export const SELLER_ME_QUERY_KEY = ['seller-application-me'] as const;

export type SellerAccountType =
  | 'LEGAL_ENTITY'
  | 'INDIVIDUAL_ENTREPRENEUR'
  | 'SELF_EMPLOYED';

export type SellerTaxSystem =
  | 'USN_INCOME'
  | 'USN_INCOME_EXPENSE'
  | 'OSNO'
  | 'NPD';

export type SellerApplicationStatus =
  | 'DRAFT'
  | 'PENDING_MODERATION'
  | 'APPROVED'
  | 'REJECTED';

/** Совпадает с Prisma enum на бэкенде. */
export type SellerApplicationRejectionKind =
  | 'POOR_DOCUMENT_PHOTOS'
  | 'DATA_MISMATCH'
  | 'INCOMPLETE_DOCUMENTS'
  | 'REQUIRES_REVISION';

export interface SellerProfileDeletionCabinetInfo {
  requestedAt: string;
  scheduledAt: string;
  deletedAt?: string;
}

export interface SellerShopSummary {
  id: number;
  shopName: string;
  slug: string;
  profileHidden?: boolean;
  profileDeletion?: SellerProfileDeletionCabinetInfo | null;
  accountBlock?: SellerAccountBlockCabinetInfo | null;
}

/** Пункт подсказки DaData suggest/party — выбор → findById по ИНН (+ КПП для филиала). */
export interface PartySuggestOption {
  id: string;
  label: string;
  inn: string;
  kpp: string | null;
}

/** Элемент списка DaData suggest/bank — выбор → подстановка БИК, корр. счёта, названия */
export interface BankSuggestOption {
  id: string;
  label: string;
  bik: string;
  correspondentAccount: string | null;
  bankName: string;
}

export type SellerProfileChangeField = 'shopName' | 'activityDescription' | 'logo';

export interface SellerApplication {
  id: number;
  publicToken: string;
  status: SellerApplicationStatus;
  currentStep: number;
  createdAt: string;
  updatedAt: string;
  sellerId: number | null;
  moderatedAt: string | null;
  /** Есть в ответе GET /me при include seller на бэкенде */
  seller?: SellerShopSummary | null;
  accountType: SellerAccountType | null;
  selfEmployedAnnualTurnoverLimitRub: string | null;
  /** Устарело для регистрации; может возвращаться из БД */
  selfEmployedTbankPartnerAcknowledged?: boolean;
  tbankSeRecipientId?: string | null;
  tbankSeRecipientStatus?: string | null;
  tbankSeAddError?: string | null;
  inn: string | null;
  kpp: string | null;
  ogrn: string | null;
  companyName: string | null;
  legalAddress: string | null;
  companyStatusText: string | null;
  bankAccountNumber: string | null;
  correspondentAccount: string | null;
  bik: string | null;
  bankName: string | null;
  taxSystem: SellerTaxSystem | null;
  directorFullName: string | null;
  passportSeries: string | null;
  passportNumber: string | null;
  passportIssuedBy: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  egrulExtractFileUrls: string[];
  passportScanFileUrls: string[];
  /** PDF договора-оферты с подставленными реквизитами (S3) */
  offerPdfFileUrl: string | null;
  offerAccepted: boolean;
  offerAcceptedAt: string | null;
  offerTemplateVersion: number | null;
  offerTemplateUpdatedAt: string | null;
  logoFileUrl: string | null;
  /** Название магазина на витрине */
  shopName: string | null;
  activityDescription: string | null;
  /** Изменения витрины на модерации (null — поле не менялось); на витрине пока прежние значения */
  pendingShopName?: string | null;
  pendingActivityDescription?: string | null;
  pendingLogoFileUrl?: string | null;
  profileChangesSubmittedAt?: string | null;
  /** Последний отказ модератора по изменениям витрины */
  profileChangesRejectionReason?: string | null;
  profileChangesRejectedAt?: string | null;
  /** Отклонённые поля витрины: значение, которое не прошло модерацию, и причина */
  profileChangesRejected?: Partial<
    Record<SellerProfileChangeField, { value: string | null; reason: string }>
  > | null;
  submittedAt: string | null;
  moderationRejectionReason: string | null;
  moderationRejectionKind?: SellerApplicationRejectionKind | null;
  moderationRejectionComment?: string | null;
  /** Связанный пользователь в GET /me */
  user?: {
    hideSellerApprovedOnboardingBanner?: boolean;
    /** Телефон аккаунта (вход / регистрация) */
    phone?: string | null;
    /** Email аккаунта (вход / регистрация); не путать с contactEmail */
    email?: string | null;
  } | null;
  /** OWNER — владелец магазина; STAFF — сотрудник с ограниченными разделами */
  cabinetRole?: 'OWNER' | 'STAFF';
  staffSections?: Array<
    | 'ORDERS'
    | 'PRODUCTS'
    | 'REVIEWS'
    | 'CONTACTS'
    | 'PROMOTION'
    | 'PROMO_CODES'
    | 'BONUSES'
    | 'FINANCE'
  > | null;
  /** Точки для заказов; у владельца null (все точки) */
  staffPickupPointIds?: number[] | null;
  /** Общий рейтинг магазина на витрине */
  averageRating?: number | null;
  approvedReviewCount?: number;
}

export interface SellerOfferDocument {
  id: number;
  templateVersion: number;
  templateUpdatedAt: string;
  pdfFileUrl: string;
  acceptedAt: string | null;
  generatedAt: string;
}

export type SellerApplicationUpdate = Partial<{
  currentStep: number;
  accountType: SellerAccountType;
  selfEmployedAnnualTurnoverLimitRub: number;
  inn: string;
  kpp: string;
  ogrn: string;
  companyName: string;
  legalAddress: string;
  companyStatusText: string;
  bankAccountNumber: string;
  correspondentAccount: string;
  bik: string;
  bankName: string;
  taxSystem: SellerTaxSystem;
  directorFullName: string;
  passportSeries: string;
  passportNumber: string;
  passportIssuedBy: string;
  contactPhone: string;
  contactEmail: string;
  egrulExtractFileUrls: string[];
  passportScanFileUrls: string[];
  offerAccepted: boolean;
  logoFileUrl: string | null;
  shopName: string;
  activityDescription: string;
}>;

export const sellerOnboardingApi = {
  /** RQ v5: `undefined` из queryFn не даёт обычный retry; пустой body отклоняем явно. */
  getMe: async () => {
    const res = await axiosInstance.get<SellerApplication>('/seller-applications/me');
    const data = res.data;
    if (data == null) {
      throw new Error('Пустой ответ при загрузке заявки');
    }
    return data;
  },

  update: (body: SellerApplicationUpdate) =>
    axiosInstance.patch<SellerApplication>('/seller-applications/me', body).then((r) => r.data),

  submit: () =>
    axiosInstance.post<SellerApplication>('/seller-applications/me/submit').then((r) => r.data),

  /** Временная ссылка на свой паспорт / ЕГРЮЛ / оферту — файлы в закрытом бакете. */
  documentLink: (url: string) =>
    axiosInstance
      .post<{ url: string }>('/seller-applications/me/document-link', { url })
      .then((r) => r.data.url),

  dismissProfileChangesRejection: () =>
    axiosInstance
      .post<SellerApplication>('/seller-applications/me/dismiss-profile-changes-rejection')
      .then((r) => r.data),

  cancelProfileChanges: (field: SellerProfileChangeField) =>
    axiosInstance
      .post<SellerApplication>('/seller-applications/me/cancel-profile-changes', { field })
      .then((r) => r.data),

  /** Сгенерировать PDF оферты из сохранённых реквизитов, загрузить в S3; при перегенерации снимает согласие. */
  generateOfferPdf: () =>
    axiosInstance
      .post<SellerApplication>('/seller-applications/me/generate-offer-pdf')
      .then((r) => r.data),

  acceptOffer: () =>
    axiosInstance
      .post<SellerApplication>('/seller-applications/me/accept-offer')
      .then((r) => r.data),

  listOfferDocuments: () =>
    axiosInstance
      .get<SellerOfferDocument[]>('/seller-applications/me/offer-documents')
      .then((r) => r.data),

  /** Узкий список: `partyType` = LEGAL только ООО/ЮЛ, INDIVIDUAL только ИП (как DaData suggest/party). */
  suggestParty: (params: {
    query: string;
    partyType?: 'LEGAL' | 'INDIVIDUAL';
  }) =>
    axiosInstance
      .post<{ suggestions: PartySuggestOption[] }>(
        '/seller-applications/dadata/suggest-party',
        params,
      )
      .then((r) => r.data),

  suggestBank: (params: { query: string }) =>
    axiosInstance
      .post<{ suggestions: BankSuggestOption[] }>(
        '/seller-applications/dadata/suggest-bank',
        params,
      )
      .then((r) => r.data),

  lookupInn: (inn: string, kpp?: string | null) =>
    axiosInstance
      .post<{
        inn: string;
        kpp: string | null;
        ogrn: string;
        companyName: string;
        legalAddress: string;
        companyStatusText: string;
        suggestedAccountType: SellerAccountType;
        directorFullName?: string;
      }>('/seller-applications/dadata/lookup-inn', {
        inn,
        ...(kpp != null && String(kpp).trim() !== '' ? { kpp: String(kpp).trim() } : {}),
      })
      .then((r) => r.data),

  checkInnAvailability: (inn: string) =>
    axiosInstance
      .post<{ available: boolean }>('/seller-applications/me/check-inn-availability', { inn })
      .then((r) => r.data),

  uploadFile: (kind: 'egrul' | 'passport' | 'logo', file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return axiosInstance
      .post<SellerApplication>(`/seller-applications/me/upload?kind=${kind}`, fd)
      .then((r) => r.data);
  },

  removeDocument: (kind: 'egrul' | 'passport' | 'logo', url: string) =>
    axiosInstance
      .post<SellerApplication>('/seller-applications/me/remove-document', { kind, url })
      .then((r) => r.data),

  dismissApprovedOnboardingBanner: () =>
    axiosInstance
      .post<SellerApplication>('/seller-applications/me/dismiss-approved-onboarding-banner')
      .then((r) => r.data),

  hideProfile: () =>
    axiosInstance
      .post<SellerApplication>('/seller-applications/me/hide-profile')
      .then((r) => r.data),

  showProfile: () =>
    axiosInstance
      .post<SellerApplication>('/seller-applications/me/show-profile')
      .then((r) => r.data),

  requestProfileDeletion: () =>
    axiosInstance
      .post<SellerApplication>('/seller-applications/me/request-profile-deletion')
      .then((r) => r.data),

  cancelProfileDeletion: () =>
    axiosInstance
      .post<SellerApplication>('/seller-applications/me/cancel-profile-deletion')
      .then((r) => r.data),
};
